import api from '../../../axios-config.js'
import type { Entitlements, FeatureFlags } from '../../composables/useEntitlements'

type FeatureFlagsApi = FeatureFlags & {
  app_access?: boolean
  marketing_kit_generation?: boolean
  image_scraping?: boolean
  per_slide_overrides?: boolean
  ai_chat_assistant?: boolean
  outreach_engine?: boolean
}

type EntitlementsApi = Partial<Entitlements> & {
  appAccess?: boolean
  app_access?: boolean
  hasAppAccess?: boolean
  has_app_access?: boolean
  features?: FeatureFlagsApi
  feature_flags?: FeatureFlagsApi
  flags?: FeatureFlagsApi
  access_mode?: string | null
}

let cachedEntitlements: Entitlements | null = null
let initialized = false
let inflightFetch: Promise<Entitlements | null> | null = null

function normalizeFeatures(rawFeatures: FeatureFlagsApi | null | undefined): FeatureFlags {
  return {
    appAccess: Boolean(rawFeatures?.appAccess ?? rawFeatures?.app_access),
    marketingKitGeneration: Boolean(rawFeatures?.marketingKitGeneration ?? rawFeatures?.marketing_kit_generation),
    imageScraping: Boolean(rawFeatures?.imageScraping ?? rawFeatures?.image_scraping),
    perSlideOverrides: Boolean(rawFeatures?.perSlideOverrides ?? rawFeatures?.per_slide_overrides),
    aiChatAssistant: Boolean(rawFeatures?.aiChatAssistant ?? rawFeatures?.ai_chat_assistant),
    enrichment: Boolean(rawFeatures?.enrichment),
    outreachEngine: Boolean(rawFeatures?.outreachEngine ?? rawFeatures?.outreach_engine),
    governancePack: Boolean((rawFeatures as any)?.governancePack ?? (rawFeatures as any)?.governance_pack),
    includedOutreachEmails: (rawFeatures as any)?.includedOutreachEmails ?? (rawFeatures as any)?.included_outreach_emails ?? null,
    includedAiCredits: (rawFeatures as any)?.includedAiCredits ?? (rawFeatures as any)?.included_ai_credits ?? null,
  }
}

function normalizeEntitlements(raw: Entitlements | null | undefined): Entitlements | null {
  if (!raw) return null

  const payload = (raw as any)?.entitlements ?? (raw as any)?.data?.entitlements ?? (raw as any)?.data ?? raw
  if (!payload) return null
  const entitlementPayload = payload as EntitlementsApi

  const normalizedFeatures = normalizeFeatures(
    entitlementPayload.features ?? entitlementPayload.flags,
  )

  // Phase D3 — Per-org rollout flags come back on `feature_flags` (dict).
  // This is intentionally separate from plan-tier `features` above. Cast
  // through unknown so we don't force the normalizer to accept both shapes.
  const rolloutFlagsRaw = (raw as any)?.feature_flags ?? (entitlementPayload as any)?.feature_flags
  const rolloutFlags: Record<string, boolean | number | string> =
    rolloutFlagsRaw && typeof rolloutFlagsRaw === 'object' && !Array.isArray(rolloutFlagsRaw)
      ? { ...rolloutFlagsRaw }
      : {}

  const status = String(entitlementPayload.status ?? '').toLowerCase()
  const accessMode = String(entitlementPayload.access_mode ?? '').toLowerCase()
  const hasStatusBackedAccess =
    (status === 'active' || status === 'trialing') &&
    (accessMode === '' || ['full', 'limited', 'grace'].includes(accessMode))
  const hasExplicitAccessFlag = Boolean(
    entitlementPayload.appAccess ??
      entitlementPayload.app_access ??
      entitlementPayload.hasAppAccess ??
      entitlementPayload.has_app_access,
  )

  return {
    status: (entitlementPayload.status as string) ?? 'unknown',
    tier: (entitlementPayload.tier as string | null) ?? null,
    current_period_end: (entitlementPayload.current_period_end as string | null) ?? null,
    cancel_at_period_end: Boolean(entitlementPayload.cancel_at_period_end),
    billing_interval: (entitlementPayload.billing_interval as string | null) ?? null,
    membership_role: (entitlementPayload as any).membership_role ?? null,
    features: {
      ...normalizedFeatures,
      appAccess: normalizedFeatures.appAccess || hasExplicitAccessFlag || hasStatusBackedAccess,
    },
    feature_flags: rolloutFlags,
  }
}

export function clearPostAuthEntitlements() {
  cachedEntitlements = null
  initialized = false
  inflightFetch = null
}

export function getPostAuthEntitlements() {
  return cachedEntitlements
}

export async function fetchPostAuthEntitlements(force = false): Promise<Entitlements | null> {
  if (inflightFetch) return inflightFetch
  if (!force && initialized) return cachedEntitlements

  inflightFetch = (async () => {
    try {
      const response = await api.get<Entitlements>('/api/me/entitlements', {
        params: { refresh: true },
        paramsSerializer: (params) => new URLSearchParams(params as Record<string, string>).toString(),
      })
      cachedEntitlements = normalizeEntitlements(response.data)
      initialized = true
      return cachedEntitlements
    } catch (err: any) {
      if (err?.response?.status === 401) {
        cachedEntitlements = null
      }
      throw err
    } finally {
      inflightFetch = null
    }
  })()

  return inflightFetch
}

/**
 * Force-refresh cached entitlements and notify sidebar/nav components
 * to re-render with updated feature flags.
 */
export async function refreshAndBroadcastEntitlements(): Promise<Entitlements | null> {
  const ent = await fetchPostAuthEntitlements(true)
  window.dispatchEvent(new CustomEvent('entitlements-updated'))
  return ent
}
